// CampaignsListPage — US-120, US-121, US-122 entry.
//
// Renders the filter bar (status pills + per-account select) + the row list
// using DS composites (`CampaignRow` from W5c.D3). The kebab menu opens a
// `DropdownMenu`; pause/resume/delete trigger the real mutations. Empty
// state mirrors the prototype copy.

import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import {
  useCampaigns,
  useDeleteCampaign,
  useLinkedinAccounts,
  usePauseCampaign,
  useResumeCampaign,
  useBusinesses,
  type Business,
  type Campaign,
  type CampaignStatus,
  type LinkedinAccountRow,
} from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import {
  Banner,
  Button,
  Card,
  CampaignRow,
  CampaignRowSkeleton,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  FilterChip,
  PageHeader,
  Select,
  toast,
  type CampaignRowData,
  type SourceKey,
} from '@barel/ui';
import { confirm } from '@/features/shell/GlobalConfirmModal';

import { buildCampaignRowCopy } from './lib/copy';

type FilterKey = 'all' | 'active' | 'paused' | 'draft' | 'finished' | 'error';

const STATUS_TO_FILTER: Record<CampaignStatus, FilterKey> = {
  draft: 'draft',
  active: 'active',
  paused: 'paused',
  paused_auto: 'paused',
  finished: 'finished',
  error: 'error',
};

function sourceTypeToSourceKey(sourceType: Campaign['sourceType']): SourceKey {
  switch (sourceType) {
    case 'salesnav':
      // Sales-nav is a "premium internal" source — render with the accent tone.
      return 'Internal';
    case 'linkedin':
      return 'Internal';
    case 'csv':
      return 'CSV';
  }
}

function toRowData(
  c: Campaign,
  account: LinkedinAccountRow | undefined,
  business: Business | undefined,
  sourceLabel: string,
): CampaignRowData {
  return {
    id: c.id,
    name: c.name,
    business: business?.name ?? '—',
    account: account?.name ?? '—',
    accountColor: account?.color ?? '#5b6ef8',
    source: sourceTypeToSourceKey(c.sourceType),
    sourceLabel,
    status: c.status,
    metrics: {
      leads: c.leadsCount ?? 0,
      invited: c.invitedCount ?? 0,
      accepted: c.acceptedCount ?? 0,
      replied: c.repliedCount ?? 0,
      hot: c.hotCount ?? 0,
    },
    dailyLimit: c.dailyLimit,
    daysRunning: c.daysRunning,
    pauseReason: c.pauseReason ?? undefined,
    errorReason: c.errorReason ?? undefined,
  };
}

export function CampaignsListPage() {
  const t = useT('campaigns');
  const navigate = useNavigate();
  const campaigns = useCampaigns();
  const accounts = useLinkedinAccounts();
  const businesses = useBusinesses();

  const [filter, setFilter] = useState<FilterKey>('all');
  const [accountFilter, setAccountFilter] = useState<string>('all');
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const pauseMut = usePauseCampaign();
  const resumeMut = useResumeCampaign();
  const deleteMut = useDeleteCampaign();

  const accountById = useMemo(
    () => new Map((accounts.data?.accounts ?? []).map((a) => [a.id, a] as const)),
    [accounts.data?.accounts],
  );
  const businessById = useMemo(
    () => new Map((businesses.data?.businesses ?? []).map((b) => [b.id, b] as const)),
    [businesses.data?.businesses],
  );

  const list = useMemo(() => campaigns.data?.campaigns ?? [], [campaigns.data?.campaigns]);

  const counts = useMemo(() => {
    const out: Record<FilterKey, number> = {
      all: list.length,
      active: 0,
      paused: 0,
      draft: 0,
      finished: 0,
      error: 0,
    };
    for (const c of list) {
      const k = STATUS_TO_FILTER[c.status];
      if (k && k !== 'all') out[k] = (out[k] ?? 0) + 1;
    }
    return out;
  }, [list]);

  const filtered = list.filter((c) => {
    if (filter !== 'all' && STATUS_TO_FILTER[c.status] !== filter) return false;
    if (accountFilter !== 'all' && c.linkedinAccountId !== accountFilter) return false;
    return true;
  });

  const accountList = (accounts.data?.accounts ?? []).filter((a) =>
    list.some((c) => c.linkedinAccountId === a.id),
  );

  const copy = useMemo(() => buildCampaignRowCopy(t), [t]);

  const onPause = async (c: Campaign) => {
    setOpenMenuId(null);
    try {
      await pauseMut.mutateAsync({ id: c.id });
      toast.success({ title: t('toast.paused') });
    } catch {
      toast.error({ title: t('toast.error') });
    }
  };

  const onResume = async (c: Campaign) => {
    setOpenMenuId(null);
    try {
      await resumeMut.mutateAsync({ id: c.id });
      toast.success({ title: t('toast.resumed') });
    } catch {
      toast.error({ title: t('toast.error') });
    }
  };

  const onDelete = async (c: Campaign) => {
    setOpenMenuId(null);
    const ok = await confirm({
      title: t('delete.modalTitle', { name: c.name }),
      body: t('delete.modalBody'),
      confirmLabel: t('delete.confirm'),
      cancelLabel: t('delete.cancel'),
      tone: 'destructive',
    });
    if (!ok) return;
    try {
      await deleteMut.mutateAsync({ id: c.id });
      toast.success({ title: t('toast.deleted') });
    } catch {
      toast.error({ title: t('toast.error') });
    }
  };

  return (
    <section className="flex flex-col gap-6" data-testid="campaigns-list">
      <PageHeader
        title={t('list.title')}
        actions={
          <Button
            variant="primary"
            size="md"
            onClick={() => {
              void navigate({ to: '/campaigns/new' });
            }}
            data-testid="campaigns-list-new"
          >
            {t('list.newButton')}
          </Button>
        }
      />

      {/* Filter bar — status pills + per-account select. */}
      <div className="flex flex-wrap items-center gap-3" data-testid="campaigns-filter-bar">
        <div className="flex flex-wrap items-center gap-1.5">
          {(
            [
              { id: 'all' as FilterKey, label: t('list.filters.all'), n: counts.all },
              { id: 'active' as FilterKey, label: t('list.filters.active'), n: counts.active },
              { id: 'paused' as FilterKey, label: t('list.filters.paused'), n: counts.paused },
              { id: 'draft' as FilterKey, label: t('list.filters.draft'), n: counts.draft },
              {
                id: 'finished' as FilterKey,
                label: t('list.filters.finished'),
                n: counts.finished,
              },
              counts.error > 0
                ? { id: 'error' as FilterKey, label: t('list.filters.error'), n: counts.error }
                : null,
            ].filter(Boolean) as { id: FilterKey; label: string; n: number }[]
          ).map((tab) => (
            <FilterChip
              key={tab.id}
              size="sm"
              tone={tab.id === 'error' ? 'error' : 'neutral'}
              selected={filter === tab.id}
              onClick={() => setFilter(tab.id)}
              data-testid={`campaigns-filter-${tab.id}`}
            >
              {`${tab.label as string} (${tab.n})`}
            </FilterChip>
          ))}
        </div>

        {accountList.length > 1 ? (
          <Select value={accountFilter} onValueChange={setAccountFilter}>
            <Select.Trigger size="sm" data-testid="campaigns-account-filter">
              <Select.Value placeholder={t('list.filters.allAccounts') as string} />
            </Select.Trigger>
            <Select.Content>
              <Select.Item value="all">{t('list.filters.allAccounts')}</Select.Item>
              {accountList.map((a) => (
                <Select.Item key={a.id} value={a.id}>
                  {a.name}
                </Select.Item>
              ))}
            </Select.Content>
          </Select>
        ) : null}
      </div>

      {campaigns.isLoading ? (
        <div className="flex flex-col gap-3" data-testid="campaigns-list-loading">
          <CampaignRowSkeleton aria-label={t('list.loadingLabel') as string} />
          <CampaignRowSkeleton aria-label={t('list.loadingLabel') as string} />
        </div>
      ) : null}

      {campaigns.isError ? (
        <Banner tone="error" title={t('list.errorTitle')} data-testid="campaigns-list-error">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>{t('list.errorBody')}</span>
            <Button
              variant="secondary"
              size="sm"
              loading={campaigns.isFetching}
              onClick={() => void campaigns.refetch()}
              data-testid="campaigns-list-retry"
            >
              {t('list.retry')}
            </Button>
          </div>
        </Banner>
      ) : null}

      {!campaigns.isLoading && !campaigns.isError && list.length === 0 ? (
        <Card padding="lg" radius="2xl" data-testid="campaigns-list-empty">
          <EmptyState
            tone="accent"
            media={<span aria-hidden="true">🎯</span>}
            title={t('list.emptyTitle')}
            description={t('list.emptySubtitle')}
            action={
              <Button
                variant="primary"
                size="md"
                onClick={() => {
                  void navigate({ to: '/campaigns/new' });
                }}
              >
                {t('list.newButton')}
              </Button>
            }
          />
        </Card>
      ) : null}

      {!campaigns.isLoading && !campaigns.isError && list.length > 0 && filtered.length === 0 ? (
        <Card padding="lg" radius="2xl" data-testid="campaigns-list-filter-empty">
          <EmptyState
            tone="neutral"
            media={<span aria-hidden="true">🔎</span>}
            title={t('list.noMatchesTitle')}
            description={t('list.noMatchesSubtitle')}
            action={
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setFilter('all');
                  setAccountFilter('all');
                }}
                data-testid="campaigns-clear-filters"
              >
                {t('list.clearFilters')}
              </Button>
            }
          />
        </Card>
      ) : null}

      {!campaigns.isLoading && !campaigns.isError && filtered.length > 0 ? (
        <div className="flex flex-col gap-3" data-testid="campaigns-list-rows">
          {filtered.map((c) => {
            const account = accountById.get(c.linkedinAccountId);
            const business = businessById.get(c.businessId);
            const sourceLabel = t(`source.${c.sourceType}`) as string;
            const data = toRowData(c, account, business, sourceLabel);
            const isOpen = openMenuId === c.id;
            return (
              <DropdownMenu
                key={c.id}
                open={isOpen}
                onOpenChange={(o) => setOpenMenuId(o ? c.id : null)}
              >
                <CampaignRow
                  campaign={data}
                  copy={copy}
                  onOpen={(id) => {
                    void navigate({ to: '/campaigns/$id', params: { id } });
                  }}
                  onKebab={(id) => setOpenMenuId(isOpen ? null : id)}
                />
                <DropdownMenuTrigger className="sr-only" data-testid={`kebab-trigger-${c.id}`} />
                <DropdownMenuPortal>
                  <DropdownMenuContent align="end" data-testid="campaigns-kebab-menu">
                    <DropdownMenuItem
                      onSelect={() => {
                        setOpenMenuId(null);
                        void navigate({ to: '/campaigns/$id', params: { id: c.id } });
                      }}
                    >
                      {t('kebab.open')}
                    </DropdownMenuItem>
                    {c.status === 'active' ? (
                      <DropdownMenuItem
                        data-testid={`kebab-pause-${c.id}`}
                        onSelect={() => void onPause(c)}
                      >
                        {t('kebab.pause')}
                      </DropdownMenuItem>
                    ) : null}
                    {c.status === 'paused' || c.status === 'paused_auto' ? (
                      <DropdownMenuItem
                        data-testid={`kebab-resume-${c.id}`}
                        onSelect={() => void onResume(c)}
                      >
                        {t('kebab.resume')}
                      </DropdownMenuItem>
                    ) : null}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      data-testid={`kebab-delete-${c.id}`}
                      onSelect={() => void onDelete(c)}
                    >
                      {t('kebab.delete')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenuPortal>
              </DropdownMenu>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}
